import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LogOut, ShieldCheck, KeyRound } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";

function initials(name: string | null, email: string): string {
  const base = name?.trim() || email;
  const parts = base.split(/[\s@.]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "U";
}

/** Auth-aware account menu: identity, active org/role, SSO self-test, sign out. */
export function UserMenu() {
  const { me } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  if (!me) return null;

  const activeOrg = me.orgs.find((o) => o.id === me.activeOrgId);

  return (
    <div className="relative ml-1">
      <button
        onClick={() => setOpen((v) => !v)}
        className="grid h-8 w-8 place-items-center rounded-full text-sm font-semibold text-white shadow-md"
        style={{ background: "linear-gradient(135deg,#f59e0b,#E89611)" }}
        title={me.user.email}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {initials(me.user.name, me.user.email)}
      </button>

      <AnimatePresence>
        {open && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ opacity: 0, y: -6, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -6, scale: 0.97 }}
              transition={{ duration: 0.15 }}
              role="menu"
              className="absolute right-0 top-11 z-50 w-64 rounded-2xl border border-app bg-surface p-2 shadow-2xl"
            >
              <div className="px-2 py-2">
                <div className="truncate text-sm font-semibold text-app">{me.user.name || me.user.email}</div>
                <div className="truncate text-xs text-app-2">{me.user.email}</div>
                {activeOrg && (
                  <div className="mt-2 flex items-center gap-1.5 text-[11px] text-app-1">
                    <ShieldCheck size={12} className="text-[hsl(var(--accent))]" />
                    {activeOrg.name} · <span className="capitalize">{activeOrg.role}</span>
                    {me.mfaEnabled && <span className="ml-1 rounded bg-surface-2 px-1.5 py-0.5 text-[10px]">MFA</span>}
                  </div>
                )}
              </div>
              <div className="my-1 h-px bg-app/60" />
              <button
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  navigate("/sso/start");
                }}
                className="flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left text-sm text-app transition hover:bg-surface-2"
              >
                <KeyRound size={15} className="text-app-1" />
                Test SSO sign-in
              </button>
              <button
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  navigate("/logout");
                }}
                className="flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left text-sm text-red-300 transition hover:bg-red-500/10"
              >
                <LogOut size={15} />
                Sign out
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
